import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { updateMetaTags } from '../utils/seo';
import { TIME, LOCATION } from './idea-landing-config';
import { PERSONAS } from './idea-persona-config';
import { expandSpintax } from '../utils/spintax';
import PricingSection from '../components/Home/PricingSection';

const IdeaPersonaLanding: React.FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { persona, timeSlug, locationSlug } = params as Record<string, string>;

  const resolved = useMemo(() => {
    const personaCfg = PERSONAS[persona];
    const time = TIME[timeSlug];
    const location = LOCATION[locationSlug];
    return { personaCfg, time, location };
  }, [persona, timeSlug, locationSlug]);

  // Redirect invalid combos
  useEffect(() => {
    if (!resolved.personaCfg || !resolved.time || !resolved.location) {
      navigate('/business-ideas/for');
    }
  }, [resolved, navigate]);

  // SEO
  useEffect(() => {
    if (!resolved.personaCfg || !resolved.time || !resolved.location) return;
    const title = `${resolved.personaCfg.title} • ${resolved.time.label} • ${resolved.location.label} | Explanatum`;
    const description = resolved.personaCfg.description;
    const url = `https://explanatum.co.uk/business-ideas/for/${persona}/${timeSlug}/${locationSlug}`;
    // Choose primary canonical as weekend + remote to avoid duplicate variants
    const isPrimary = timeSlug === 'weekend' && locationSlug === 'remote';
    const canonical = isPrimary
      ? url
      : `https://explanatum.co.uk/business-ideas/for/${persona}/weekend/remote`;
    updateMetaTags({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      ogUrl: url,
      twitterUrl: url,
      canonicalUrl: canonical,
      ogImage: 'https://explanatum.co.uk/og-image.jpg',
      // If not primary and content delta is small, prefer not to index
      robots: isPrimary ? 'index,follow' : 'noindex,follow',
    });
    // Analytics
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'view_persona_landing', {
          page_location: url,
          persona_slug: persona,
          time_slug: timeSlug,
          location_slug: locationSlug,
        });
      }
    } catch {}
  }, [persona, timeSlug, locationSlug, resolved]);

  if (!resolved.personaCfg || !resolved.time || !resolved.location) return null;

  // Deterministic spintax intro
  const seedKey = `${persona}|${timeSlug}|${locationSlug}`;
  const introText = (resolved.personaCfg.introVariants && resolved.personaCfg.introVariants.length > 0)
    ? expandSpintax(resolved.personaCfg.introVariants[0], seedKey)
    : resolved.personaCfg.description;

  const skillIds = resolved.personaCfg.defaultSkillIds?.join(',') || '';
  const prefillQuery = new URLSearchParams({
    ...(skillIds ? { skills: skillIds } : {}),
    time: resolved.time.id,
    work: resolved.location.ids.join(','),
    scope: resolved.location.scope,
  }).toString();

  const startHref = `/idea-generator?${prefillQuery}`;

  const onStartClick = () => {
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'click_start_generator_prefilled', {
          source: 'persona_landing',
          persona_slug: persona,
          time_slug: timeSlug,
          location_slug: locationSlug,
        });
      }
    } catch {}
  };

  const onTrialClick = () => {
    try {
      // @ts-ignore
      if (window && typeof window.gtag === 'function') {
        // @ts-ignore
        window.gtag('event', 'click_free_trial_cta', {
          source: 'persona_landing',
          persona_slug: persona,
          time_slug: timeSlug,
          location_slug: locationSlug,
        });
      }
    } catch {}
  };

  // JSON-LD
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${resolved.personaCfg.title} Business Ideas (${resolved.time.label}, ${resolved.location.label})`,
    description: resolved.personaCfg.description,
    url: `https://explanatum.co.uk/business-ideas/for/${persona}/${timeSlug}/${locationSlug}`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://explanatum.co.uk/' },
        { '@type': 'ListItem', position: 2, name: 'Business Ideas', item: 'https://explanatum.co.uk/business-ideas' },
        { '@type': 'ListItem', position: 3, name: 'For Personas', item: 'https://explanatum.co.uk/business-ideas/for' },
        { '@type': 'ListItem', position: 4, name: `${resolved.personaCfg.title} • ${resolved.time.label} • ${resolved.location.label}` },
      ],
    },
  };

  const faqJsonLd = resolved.personaCfg.faqs && resolved.personaCfg.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: resolved.personaCfg.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  return (
    <main className="min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-gray-50 via-white to-indigo-50" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-200/30 blur-3xl" />

      <article className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <header className="mb-10">
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center gap-2 text-sm text-gray-600">
              <li><Link to="/" className="hover:text-gray-900 transition-colors">Home</Link></li>
              <li aria-hidden="true" className="text-gray-400">/</li>
              <li><Link to="/business-ideas" className="hover:text-gray-900 transition-colors">Business Ideas</Link></li>
              <li aria-hidden="true" className="text-gray-400">/</li>
              <li><Link to="/business-ideas/for" className="hover:text-gray-900 transition-colors">For Personas</Link></li>
              <li aria-hidden="true" className="text-gray-400">/</li>
              <li aria-current="page" className="font-semibold text-gray-800">{resolved.personaCfg.title} • {resolved.time.label} • {resolved.location.label}</li>
            </ol>
          </nav>

          <div className="relative overflow-hidden rounded-2xl border border-gray-200/60 bg-white/70 backdrop-blur-sm shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-transparent to-purple-500/5" />
            <div className="relative p-6 sm:p-10">
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                <span className="bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                  {resolved.personaCfg.h1}
                </span>
                <span className="block text-gray-800 mt-1 text-xl sm:text-2xl">
                  for {resolved.time.label} • {resolved.location.label}
                </span>
              </h1>
              <p className="mt-4 text-lg text-gray-700 max-w-3xl">
                {introText}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 text-sm">{resolved.personaCfg.title}</span>
                <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1 text-sm">{resolved.time.label}</span>
                <span className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-3 py-1 text-sm">{resolved.location.label}</span>
              </div>
            </div>
          </div>
        </header>

        <section aria-labelledby="start-generator" className="my-10">
          <div className="relative overflow-hidden rounded-2xl bg-white border border-gray-200/70 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-blue-500/5" />
            <div className="relative p-6 sm:p-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h2 id="start-generator" className="text-xl font-semibold text-gray-900">Generate tailored ideas</h2>
                  <p className="text-gray-700 mt-1">Kick off with this setup. We cover 7,000+ skills and budgets from £1 to multi-million investment paths. Tweak skills and constraints in the wizard.</p>
                </div>
                <Link
                  to={startHref}
                  onClick={onStartClick}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-medium shadow hover:shadow-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-indigo-500"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5"><path d="M2.25 12c0-.41.25-.78.63-.93l16.5-6.75a.97.97 0 0 1 1.32 1.08l-2.31 9.24a.97.97 0 0 1-.58.68l-6.15 2.46 2.46 6.15a.97.97 0 0 1-1.66.98l-6.75-16.5A.96.96 0 0 1 2.25 12Z" /></svg>
                  Start Idea Generator
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Unified 7‑day free trial signup */}
        <section aria-labelledby="free-trial" id="free-trial" className="my-14">
          <PricingSection />
        </section>

        <section aria-labelledby="how-it-helps" className="my-10">
          <div className="relative overflow-hidden rounded-2xl bg-white/70 border border-gray-200/60 shadow-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-blue-500/5" />
            <div className="relative p-6 sm:p-8">
              <h2 id="how-it-helps" className="text-xl font-semibold text-gray-900">How Explanatum helps</h2>
              <ul className="mt-3 grid sm:grid-cols-2 gap-3">
                {resolved.personaCfg.howHelps.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700">
                    <span className="mt-1 h-2 w-2 rounded-full bg-indigo-500" aria-hidden="true" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Examples block */}
        {resolved.personaCfg.exampleIdeas && resolved.personaCfg.exampleIdeas.length > 0 && (
          <section aria-labelledby="examples" className="my-10">
            <h2 id="examples" className="text-xl font-semibold text-gray-900 mb-4">Concrete examples tailored to you</h2>
            <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {resolved.personaCfg.exampleIdeas.map((ex, idx) => (
                <li key={idx} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="font-medium text-gray-900">{ex.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{ex.whyFit}</p>
                  <ol className="mt-2 list-decimal list-inside text-sm text-gray-700 space-y-1">
                    {ex.steps.map((s, i) => (<li key={i}>{s}</li>))}
                  </ol>
                  <div className="mt-2 text-xs text-gray-600">
                    {ex.tools && ex.tools.length > 0 && (<p><span className="font-semibold">Tools:</span> {ex.tools.join(', ')}</p>)}
                    {ex.costRange && (<p><span className="font-semibold">Typical startup cost:</span> {ex.costRange}</p>)}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Mini playbook */}
        {resolved.personaCfg.playbookByTime && resolved.personaCfg.playbookByTime[timeSlug] && (
          <section aria-labelledby="playbook" className="my-10">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 id="playbook" className="text-xl font-semibold text-gray-900">Mini playbook for {resolved.time.label.toLowerCase()}</h2>
              <ol className="mt-3 list-decimal list-inside space-y-1 text-gray-700">
                {resolved.personaCfg.playbookByTime[timeSlug].map((step, i) => (<li key={i}>{step}</li>))}
              </ol>
            </div>
          </section>
        )}

        {/* Coverage and budget reassurance */}
        <section aria-labelledby="coverage" className="my-10">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 id="coverage" className="text-xl font-semibold text-gray-900">Covers 7,000+ skills • Works from £1 to multi‑million budgets</h2>
            <p className="text-gray-700 mt-2">Our idea generator and playbooks adapt to your skill mix, time, and work setup. Start with £1 validation pathways and scale to larger investments with risk gates, pricing tools, and outreach modules.</p>
          </div>
        </section>

        <section aria-labelledby="popular-variations" className="my-10">
          <h2 id="popular-variations" className="text-xl font-semibold text-gray-900 mb-4">Popular variations</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(TIME).map(([slug, t]) => (
              <li key={slug}>
                <Link
                  to={`/business-ideas/for/${persona}/${slug}/${locationSlug}`}
                  className="group block relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/0 to-indigo-500/0 group-hover:from-blue-500/5 group-hover:to-indigo-500/5 transition" />
                  <div className="relative z-10">
                    <p className="font-medium text-gray-900">{resolved.personaCfg.title} • {t.label} • {resolved.location.label}</p>
                    <p className="mt-1 text-sm text-gray-600">Try a different time commitment with the same persona and location.</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <aside aria-labelledby="explore-skills" className="my-10">
          <h2 id="explore-skills" className="text-xl font-semibold text-gray-900 mb-4">Explore skill-based ideas</h2>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {['web-development','ai-ml','marketing','design','no-code','data-analytics'].map((skillSlug) => (
              <li key={skillSlug}>
                <Link
                  to={`/business-ideas/${skillSlug}/${timeSlug}/${locationSlug}`}
                  className="group block relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-500/0 to-pink-500/0 group-hover:from-purple-500/5 group-hover:to-pink-500/5 transition" />
                  <div className="relative z-10">
                    <p className="font-medium text-gray-900">Skill bundle: {skillSlug.replace('-', ' ')}</p>
                    <p className="mt-1 text-sm text-gray-600">Browse curated ideas by skill with your current constraints.</p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </aside>

        {/* Hidden semantic helper content for SEO */}
        <section aria-hidden="true" className="sr-only">
          <h2>Semantic topics</h2>
          <p>business ideas for {resolved.personaCfg.title.toLowerCase()}, {resolved.time.label.toLowerCase()}, {resolved.location.label.toLowerCase()}, side hustle, startup ideas</p>
        </section>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      {faqJsonLd && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      )}
    </main>
  );
};

export default IdeaPersonaLanding;
